/* ===========================================================
   Registro de tenants da plataforma — fonte da verdade do painel
   de operador. Dado PURO (sem StoreData), para ser importado tanto
   por tenants.ts (resolver a loja sintética da impersonação) quanto
   por mocks/admin.ts (métricas e contas) sem ciclo de import.

   A entrada `koloni-skateshop` é a loja-demo real (sizeFactor 1, dados
   de koloni-skateshop.ts). As demais são sintéticas: a tela do lojista,
   ao impersonar, recebe a loja-demo reescalada por `sizeFactor` com
   nome/segmento próprios (synthetic-store.ts) — simplificação de demo
   registrada em database-schema.md §6.

   Datas ISO (convenção do contrato). "Hoje" da demo: 2026-06-10.
   =========================================================== */
// Tipos antes em api/admin-types (removido do site público) — inline p/ isolamento (ADR §38).
type TenantStatus = "trial" | "active" | "expired" | "canceled";
interface AdminConnection {
  provider: string;
  kind: string;
  displayName: string;
  status: string;
  lastSyncAt: string | null;
  statusMessage?: string;
}
import type { FaixaId, TenantRef } from "../api/types";

export interface TenantRegistryEntry {
  tenant: TenantRef;
  name: string;
  segment: string;
  /** Faixa do plano único (decisions.md §20) ou "trial". Coerência com a
   *  faixa re-detectada da loja sintética (sizeFactor) — ver admin.test.ts. */
  planId: FaixaId | "trial";
  status: TenantStatus;
  /** Receita recorrente mensal nossa — 0 em trial/expirado/cancelado. */
  mrr: number;
  /** Escala da loja-demo na impersonação (1 = Koloni Skate Shop real). */
  sizeFactor: number;
  usersCount: number;
  createdAt: string;
  trialEndsAt: string | null;
  lastActivityAt: string;
  /** Assinatura ativa com cobrança falha (dunning) → entra em "em risco". */
  paymentFailed?: boolean;
  connections: AdminConnection[];
}

const ns = (
  displayName: string,
  lastSyncAt: string | null,
  extra?: Partial<AdminConnection>,
): AdminConnection => ({
  provider: "nuvemshop",
  kind: "sales_channel",
  displayName,
  status: "active",
  lastSyncAt,
  ...extra,
});

export const TENANT_REGISTRY: TenantRegistryEntry[] = [
  {
    tenant: { accountId: "acc_demo", storeId: "koloni-skateshop" },
    name: "Koloni Skate Shop",
    segment: "Skate e streetwear",
    planId: "f2",
    status: "active",
    mrr: 397,
    sizeFactor: 1,
    usersCount: 3,
    createdAt: "2025-11-02",
    trialEndsAt: null,
    lastActivityAt: "2026-06-10T12:40:00-03:00",
    connections: [
      ns("Koloni Skate Shop", "2026-06-10T12:35:00-03:00"),
      {
        provider: "meta_ads",
        kind: "ad_platform",
        displayName: "Koloni Skate Shop — Meta",
        status: "active",
        lastSyncAt: "2026-06-10T06:00:00-03:00",
      },
      {
        provider: "google_ads",
        kind: "ad_platform",
        displayName: "Koloni Skate Shop — Google",
        status: "active",
        lastSyncAt: "2026-06-10T06:00:00-03:00",
      },
      {
        provider: "ga4",
        kind: "analytics",
        displayName: "GA4 — koloniskateshop.com",
        status: "active",
        lastSyncAt: "2026-06-10T06:00:00-03:00",
      },
    ],
  },
  {
    tenant: { accountId: "acc_bossa", storeId: "atelie-bossa" },
    name: "Ateliê Bossa",
    segment: "Moda feminina",
    planId: "f2",
    status: "active",
    mrr: 397,
    sizeFactor: 0.58,
    usersCount: 1,
    createdAt: "2026-01-15",
    trialEndsAt: null,
    lastActivityAt: "2026-06-09T18:12:00-03:00",
    connections: [ns("Ateliê Bossa", "2026-06-10T05:50:00-03:00")],
  },
  {
    tenant: { accountId: "acc_verdelar", storeId: "verde-lar" },
    name: "Verde Lar Casa & Jardim",
    segment: "Casa e decoração",
    planId: "f3",
    status: "active",
    mrr: 697,
    sizeFactor: 1.85,
    usersCount: 3,
    createdAt: "2025-12-08",
    trialEndsAt: null,
    lastActivityAt: "2026-06-10T11:05:00-03:00",
    connections: [
      ns("Verde Lar", "2026-06-10T05:55:00-03:00"),
      {
        provider: "meta_ads",
        kind: "ad_platform",
        displayName: "Verde Lar — Meta",
        status: "active",
        lastSyncAt: "2026-06-10T06:00:00-03:00",
      },
    ],
  },
  {
    tenant: { accountId: "acc_petisco", storeId: "petisco-cia" },
    name: "Petisco & Cia",
    segment: "Pet shop",
    planId: "trial",
    status: "trial",
    mrr: 0,
    sizeFactor: 0.9,
    usersCount: 1,
    createdAt: "2026-06-02",
    trialEndsAt: "2026-06-12",
    lastActivityAt: "2026-06-10T09:48:00-03:00",
    connections: [ns("Petisco & Cia", "2026-06-10T05:40:00-03:00")],
  },
  {
    tenant: { accountId: "acc_norte", storeId: "norte-suplementos" },
    name: "Norte Suplementos",
    segment: "Suplementos",
    planId: "f3",
    status: "active",
    mrr: 697,
    sizeFactor: 2.4,
    usersCount: 2,
    createdAt: "2025-10-21",
    trialEndsAt: null,
    lastActivityAt: "2026-06-10T10:22:00-03:00",
    connections: [
      ns("Norte Suplementos", "2026-06-10T05:45:00-03:00"),
      {
        provider: "meta_ads",
        kind: "ad_platform",
        displayName: "Norte — Meta",
        status: "error",
        lastSyncAt: "2026-06-08T06:00:00-03:00",
        statusMessage: "Token expirado — reconectar",
      },
      {
        provider: "google_ads",
        kind: "ad_platform",
        displayName: "Norte — Google",
        status: "active",
        lastSyncAt: "2026-06-10T06:00:00-03:00",
      },
    ],
  },
  {
    tenant: { accountId: "acc_bella", storeId: "bella-pelle" },
    name: "Bella Pelle Cosméticos",
    segment: "Cosméticos",
    planId: "trial",
    status: "trial",
    mrr: 0,
    sizeFactor: 1.2,
    usersCount: 1,
    createdAt: "2026-06-08",
    trialEndsAt: "2026-06-18",
    lastActivityAt: "2026-06-10T08:30:00-03:00",
    connections: [ns("Bella Pelle", "2026-06-10T05:30:00-03:00")],
  },
  {
    tenant: { accountId: "acc_lumi", storeId: "lumi-joias" },
    name: "Lumi Joias",
    segment: "Joias e acessórios",
    planId: "f2",
    status: "active",
    mrr: 397,
    sizeFactor: 0.8,
    usersCount: 1,
    createdAt: "2026-02-19",
    trialEndsAt: null,
    lastActivityAt: "2026-06-10T07:15:00-03:00",
    paymentFailed: true,
    connections: [ns("Lumi Joias", "2026-06-10T05:35:00-03:00")],
  },
  {
    tenant: { accountId: "acc_cantinho", storeId: "cantinho-cafe" },
    name: "Cantinho do Café",
    segment: "Alimentos e bebidas",
    planId: "trial",
    status: "expired",
    mrr: 0,
    sizeFactor: 0.5,
    usersCount: 1,
    createdAt: "2026-05-06",
    trialEndsAt: "2026-05-20",
    lastActivityAt: "2026-05-19T16:44:00-03:00",
    connections: [ns("Cantinho do Café", "2026-05-19T16:40:00-03:00", { status: "paused" })],
  },
  {
    tenant: { accountId: "acc_maqsul", storeId: "maquinario-sul" },
    name: "Maquinário Sul",
    segment: "Ferramentas",
    planId: "f2",
    status: "canceled",
    mrr: 0,
    sizeFactor: 1.4,
    usersCount: 2,
    createdAt: "2025-09-10",
    trialEndsAt: null,
    lastActivityAt: "2026-05-28T14:10:00-03:00",
    connections: [ns("Maquinário Sul", "2026-05-28T14:05:00-03:00", { status: "revoked" })],
  },
];

/** "Hoje" da demo — base dos cálculos relativos (trial expirando, novos no mês). */
export const HOJE = "2026-06-10";
